import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import ENV from 'tio-employee/config/environment';
import type {
  Stripe,
  StripeElements,
  StripeConstructorOptions,
  StripeIssuingCardNumberDisplayElement,
  StripeIssuingCardCvcDisplayElement,
  StripeIssuingCardExpiryDisplayElement,
  StripeIssuingCardPinDisplayElement,
} from '@stripe/stripe-js';

/**
 * Stripe Issuing "display" elements (`stripe-card-display.gts` /
 * `card-details-tab.gts`) mount Stripe.js secure iframes that render the real
 * card number/CVV/expiry/PIN — the raw values never touch app JS, the DOM as
 * plain text, or any API JSON response (confirmed: no `cardNumber`/`cvv`
 * field exists on the `pcs-card` resource). This module holds the shared,
 * security-critical plumbing (ephemeral-key exchange/refresh, element
 * lifecycle) so there is exactly one implementation of it; each consuming
 * component keeps its own template/layout.
 */

export type IssuingName =
  | 'issuingCardNumberDisplay'
  | 'issuingCardCvcDisplay'
  | 'issuingCardExpiryDisplay'
  | 'issuingCardPinDisplay';

export type AnyIssuingEl =
  | StripeIssuingCardNumberDisplayElement
  | StripeIssuingCardCvcDisplayElement
  | StripeIssuingCardExpiryDisplayElement
  | StripeIssuingCardPinDisplayElement;

export type IssuingAuth = { nonce: string; ephemeralKeySecret: string };

/**
 * Why a card's details can't be shown. `denied` and `expired` are the client-token endpoint's 403
 * and 401: this user may not see the card, or their sign-in has ended. Anything else (Stripe.js
 * failing to load, Stripe refusing the card, a network or server error) is `retryable`.
 */
export type CardDetailsFailure = 'denied' | 'expired' | 'retryable';

class ClientTokenError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
  }
}

const FAILURE_BY_STATUS: Record<number, CardDetailsFailure | undefined> = {
  401: 'expired',
  403: 'denied',
};

const cardNumberStyles = {
  base: {
    iconColor: '#c4f0ff',
    color: '#fff',
    fontWeight: '400',
    fontFamily: 'Montserrat, Open Sans, Segoe UI, sans-serif',
    fontSize: '24px',
  },
};

const cardDetailsStyles = {
  base: {
    iconColor: '#c4f0ff',
    color: '#fff',
    fontWeight: '400',
    fontFamily: 'Montserrat, Open Sans, Segoe UI, sans-serif',
    fontSize: '16px',
  },
};

const descriptionListStripeStyles = {
  base: {
    color: '#374151',
    fontWeight: '500',
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '14px',
    lineHeight: '1.5rem',
  },
};

// Keyed by publishable key + connected account — a single module-level
// promise would reuse the FIRST card's connected-account Stripe instance for
// every later card, silently requesting nonces/display elements against the
// wrong Stripe account once a second company's card is viewed in the same
// session.
const stripeInstances = new Map<string, Promise<Stripe | null>>();
const getStripe = (
  pk: string,
  opts: StripeConstructorOptions = {},
  loadStripeFn: (pk: string, opts?: StripeConstructorOptions) => Promise<Stripe | null>
) => {
  const cacheKey = `${pk}:${opts.stripeAccount ?? ''}`;
  let promise = stripeInstances.get(cacheKey);
  if (!promise) {
    // A transient failure (rejection, or a resolved `null` from a Stripe.js
    // load failure) must NOT stick around forever — otherwise the very next
    // card-details open (even after closing and reopening the drawer) reuses
    // the same failed promise and never retries, permanently bricking card
    // reveal for the rest of the SPA session. Evict on failure so the next
    // caller gets a fresh attempt; a successful resolution stays cached.
    promise = loadStripeFn(pk, opts).then((stripe) => {
      if (!stripe) {
        stripeInstances.delete(cacheKey);
      }
      return stripe;
    });
    promise.catch(() => {
      stripeInstances.delete(cacheKey);
    });
    stripeInstances.set(cacheKey, promise);
  }
  return promise;
};

const stripePublishableKey = {
  staging:
    'pk_test_51QVKAvGwNyD6Nr0b1MSw8J18o9gaYMSsBdhk4pM3kH4OYvuROqqMAgvBymnuBj10LLvVMW0Ndt6FHIK8jvH2zKoH00UqCMY5BA',
  production:
    'pk_live_51QVKAvGwNyD6Nr0bPB77ZhKsqDorlZfywALFLfHkxMmlN65KzZ8fW7G8WekmVNcmzmZBr75hc60pBafqpdvWHGuk00c8OKlcAJ',
};

/**
 * Attach to a container element to mount one Stripe Issuing "display" element
 * inside it. Args (in order): the element kind, a style-set key, the Stripe
 * `Elements` instance, the Issuing card's provider id, the current ephemeral
 * auth, and a callback to register the mounted element for auth refreshes.
 */
export const mountIssuing = modifier(
  (
    element: HTMLElement,
    [name, styleType, elements, issuingCardId, auth, register]: [
      IssuingName,
      'cardNumber' | 'cardDetails' | 'descriptionList',
      StripeElements,
      string,
      IssuingAuth,
      (el: AnyIssuingEl) => (() => void) | void,
    ]
  ) => {
    let stripeStyles;
    if (styleType === 'cardNumber') {
      stripeStyles = cardNumberStyles;
    } else if (styleType === 'descriptionList') {
      stripeStyles = descriptionListStripeStyles;
    } else {
      stripeStyles = cardDetailsStyles;
    }
    let issuingEl: AnyIssuingEl;

    switch (name) {
      case 'issuingCardNumberDisplay':
        issuingEl = elements.create('issuingCardNumberDisplay', {
          issuingCard: issuingCardId,
          ...auth,
          style: stripeStyles,
        });
        break;
      case 'issuingCardCvcDisplay':
        issuingEl = elements.create('issuingCardCvcDisplay', {
          issuingCard: issuingCardId,
          ...auth,
          style: stripeStyles,
        });
        break;
      case 'issuingCardExpiryDisplay':
        issuingEl = elements.create('issuingCardExpiryDisplay', {
          issuingCard: issuingCardId,
          ...auth,
          style: stripeStyles,
        });
        break;
      case 'issuingCardPinDisplay':
        issuingEl = elements.create('issuingCardPinDisplay', {
          issuingCard: issuingCardId,
          ...auth,
          style: stripeStyles,
        });
        break;
    }
    issuingEl.mount(element);
    // `auth` is a tracked positional arg, so every 14-minute credential
    // refresh reruns this modifier: the old element is torn down and a new
    // one mounted. Without unregistering the old element first, the session's
    // issuingEls set accumulates destroyed elements, and update() calls
    // against them on the next refresh throw (breaking the still-mounted
    // elements that come after the stale one in Set iteration order).
    const unregister = register?.(issuingEl);
    return () => {
      unregister?.();
      issuingEl.destroy();
    };
  }
);

/**
 * Owns one card's Stripe Issuing ephemeral-key lifecycle: exchanges a nonce
 * for an ephemeral key secret via `POST /pcs-cards/:id/client-token`,
 * refreshes it before expiry, and pushes fresh credentials to every mounted
 * display element. Not a Component — instantiate one per card-display
 * component, call `setup()` once the card's details are wanted (and again to
 * retry) and `teardown()` from `willDestroy`.
 */
export class StripeIssuingSession {
  @tracked elements!: StripeElements;
  @tracked auth!: IssuingAuth;
  /** Why Stripe or the API last refused this card's details; null while they can show. */
  @tracked failure: CardDetailsFailure | null = null;
  private issuingEls = new Set<AnyIssuingEl>();
  private refreshTimer?: number;
  // Guards against a slow setup()/refreshAuth() (still awaiting Stripe.js,
  // the dynamic import, or the token exchange) completing AFTER teardown()
  // already ran — without this, a fast open/close cycle can install a
  // refreshTimer post-teardown that nothing ever clears, leaking an interval
  // that keeps exchanging card credentials every 14 minutes.
  #isDestroyed = false;
  #onError?: (error: unknown) => void;

  constructor(
    private cardId: string,
    private stripeCardProviderId: string,
    // Injectable seam for tests — production callers never pass this; it
    // defaults to the real dynamic import. Avoids needing to mock Stripe.js's
    // ESM dynamic import directly in a browser-based QUnit run.
    private loadStripeFn: (
      pk: string,
      opts?: StripeConstructorOptions
    ) => Promise<Stripe | null> = (pk, opts) =>
      import('@stripe/stripe-js').then(({ loadStripe }) => loadStripe(pk, opts))
  ) {}

  /** True while Stripe has refused or failed to load this card's details; views show the masked card. */
  get unavailable() {
    return this.failure !== null;
  }

  /** The display elements can mount: auth and elements are in place and Stripe hasn't since refused. */
  get isReady() {
    return Boolean(this.auth && this.elements) && !this.unavailable;
  }

  private get apiHost() {
    return ENV.apiHost as string;
  }

  private get headers() {
    // ADR-001: read current auth headers at call time without an Ember owner.
    return buildTioAuthHeaders({
      apiKey: ENV.apiKey,
      contentType: 'application/json',
      accept: 'application/json',
    });
  }

  private exchangeNonce = async (cardId: string, nonce: string) => {
    const response = await fetch(
      `${this.apiHost}/pcs-cards/${encodeURIComponent(cardId)}/client-token`,
      {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({ nonce }),
      }
    );
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new ClientTokenError(
        response.status,
        `client-token ${response.status} ${response.statusText} — ${JSON.stringify(json)}`
      );
    }
    const secret = json?.secret;
    if (typeof secret !== 'string' || !secret) {
      throw new Error('Missing ephemeral key secret in response');
    }

    return secret;
  };

  /**
   * Loads Stripe.js for the company's connected account, exchanges the card's first ephemeral key
   * and refreshes it every 14 minutes. Never rejects: when Stripe.js fails to load or Stripe
   * refuses the card, now or on a later refresh, `failure` says why and `onError` receives the
   * cause. A `denied` or `expired` refresh also stops refreshing, since another try can't fix
   * either; calling `setup()` again retries, and a success replaces the earlier refresh.
   */
  async setup(companyAccountProviderId: string, onError?: (error: unknown) => void) {
    this.#onError = onError;
    try {
      const publishableKey =
        ENV.tioEnvironment === 'production'
          ? stripePublishableKey.production
          : stripePublishableKey.staging;

      const stripe = await getStripe(
        publishableKey,
        {
          stripeAccount: companyAccountProviderId,
        },
        this.loadStripeFn
      );
      if (this.#isDestroyed) return;
      if (!stripe) throw new Error('Stripe.js failed to load');
      this.elements = stripe.elements();

      await this.refreshAuth(stripe);
      if (this.#isDestroyed) return;

      // refresh just before expiration (every 15 mins)
      window.clearInterval(this.refreshTimer);
      this.refreshTimer = window.setInterval(
        () => this.refreshAuth(stripe).catch(this.#fail),
        14 * 60 * 1000
      );
    } catch (error) {
      this.#fail(error);
    }
  }

  #fail = (error: unknown) => {
    if (this.#isDestroyed) return;
    this.failure =
      (error instanceof ClientTokenError && FAILURE_BY_STATUS[error.status]) || 'retryable';
    if (this.failure !== 'retryable') window.clearInterval(this.refreshTimer);
    try {
      this.#onError?.(error);
    } catch {
      // A reporter that throws must not undo the never-rejects contract.
    }
  };

  private async refreshAuth(stripe: Stripe) {
    const { nonce } = await stripe.createEphemeralKeyNonce({
      issuingCard: this.stripeCardProviderId,
    });
    if (this.#isDestroyed) return;
    if (!nonce) throw new Error('Nonce missing');
    if (!this.cardId) throw new Error('Card ID missing');
    const ephemeralKeySecret = await this.exchangeNonce(this.cardId, nonce);
    if (this.#isDestroyed) return;
    this.auth = { nonce, ephemeralKeySecret };
    this.failure = null;
    this.issuingEls.forEach((el) => el.update(this.auth));
  }

  register = (el: AnyIssuingEl) => {
    this.issuingEls.add(el);
    return () => {
      this.issuingEls.delete(el);
    };
  };

  teardown() {
    this.#isDestroyed = true;
    window.clearInterval(this.refreshTimer);
    this.issuingEls.forEach((el) => el.destroy());
    this.issuingEls.clear();
  }
}
